// Client helper: takes an order reference, asks the server for signed
// PayFast checkout fields, then auto-submits a hidden form so the user
// goes straight to the PayFast hosted payment page (no invoice stop).
//
// If PayFast previously rejected the checkout with HTTP 400 (typically
// the "Merchant is unable to receive payments from the same account"
// error surfaced via our cancel_url), pass `forceStripEmail: true` so
// the server strips `email_address` and the retry succeeds.
import { createPayfastCheckout } from "@/lib/api/payfast.functions";

export type LaunchOptions = {
  /** Force the server to drop `email_address` before signing. */
  forceStripEmail?: boolean;
};

export async function launchPayfastCheckout(
  reference: string,
  options: LaunchOptions = {},
): Promise<void> {
  let payload: { processUrl: string; fields: Record<string, string> };
  try {
    payload = (await createPayfastCheckout({
      data: {
        reference,
        forceStripEmail: options.forceStripEmail === true,
      },
    })) as { processUrl: string; fields: Record<string, string> };
  } catch (err: any) {
    // Bubble a human-readable message the UI can show verbatim.
    const message =
      err?.message ??
      "PayFast checkout could not be prepared. Please try again in a moment.";
    throw new Error(message);
  }

  const { processUrl, fields } = payload;
  const form = document.createElement("form");
  form.method = "POST";
  form.action = processUrl;
  form.style.display = "none";
  for (const [k, v] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = String(v);
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

/**
 * Heuristic — reads the current URL for the signals PayFast leaves on our
 * cancel_url when the buyer is bounced back with a 400 (e.g. the
 * "merchant is unable to receive payments from the same account" case).
 * Used by RetryablePayfastCheckout to auto-retry with `forceStripEmail`.
 */
export function detectPayfastMerchantEmailConflict(search: string): boolean {
  try {
    const params = new URLSearchParams(search);
    const hint = (params.get("payfast_reason") || params.get("reason") || "").toLowerCase();
    return (
      hint.includes("merchant is unable") ||
      hint.includes("same account") ||
      hint.includes("email_address")
    );
  } catch {
    return false;
  }
}
